// components/import/NewImportForm.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useAuthSimple } from '../../hooks/useAuth';
import { importacaoService } from '../../services/entitiesServices';
import { ClienteForm } from '../crud/ClienteForm';
import { useNotifier } from '../ui/Notifier';
import { ApoliceFormModal } from '../apolice/ApoliceFormModal';
import { documentoService } from '../../services/entitiesServices';
import { apiClient } from '../../config/api';
import type { ImportacaoResultado } from '../../types/entities';

type ImportType = 'cliente' | 'analise' | 'multcalculo' | 'propostas' | 'migracao';

interface NewImportFormProps {
  type: ImportType;
  title: string;
  description?: string;
  acceptedFiles?: string;
}

export const NewImportForm: React.FC<NewImportFormProps> = ({ 
  type, 
  title, 
  description,
  acceptedFiles = type === 'migracao' ? '.csv' : '.xls,.xlsx'
}) => {
  const [file, setFile] = useState<File | null>(null);
  const notifier = useNotifier();
  const [tableName, setTableName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const progressTimer = useRef<number | null>(null);
  const [result, setResult] = useState<ImportacaoResultado | null>(null);
  const [arquivoNome, setArquivoNome] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [modalRaw, setModalRaw] = useState<any>(null);
  const [apoliceModalOpen, setApoliceModalOpen] = useState(false);
  const [apoliceData, setApoliceData] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [serverErrorBody, setServerErrorBody] = useState<any>(null);

  const { user } = useAuthSimple();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      setError('Por favor, selecione um arquivo');
      return;
    }

    if (type === 'migracao' && !tableName.trim()) {
      setError('Por favor, informe o nome da tabela');
      return;
    }

    setLoading(true);
    // Start a simulated progress animation (will be completed when request finishes)
    setProgress(5);
    if (progressTimer.current) window.clearInterval(progressTimer.current);
    progressTimer.current = window.setInterval(() => {
      setProgress((p) => {
        // gradually increase up to 80% while loading
        const next = Math.min(80, p + Math.random() * 7 + 3);
        return next;
      });
    }, 400) as unknown as number;
    setError('');
    setResult(null);

    try {
      let importResult: ImportacaoResultado | any;

      // Debug: verificar se o arquivo está presente
      console.log('📤 Enviando arquivo:', {
        nome: file.name,
        tamanho: file.size,
        tipo: file.type,
        tipoImportacao: type
      });

      switch (type) {
        case 'cliente':
          importResult = await importacaoService.importarClientes(file);
          break;
        case 'analise':
          importResult = await importacaoService.importarAnalise(file);
          break;
        case 'multcalculo':
          console.log('📤 Chamando importarMultCalculo com arquivo:', file);
          importResult = await importacaoService.importarMultCalculo(file);
          break;
        case 'propostas':
          importResult = await importacaoService.importarPropostas(file);
          break;
        case 'migracao':
          importResult = await importacaoService.importarMigracao(file, tableName);
          break;
        default:
          throw new Error('Tipo de importação não suportado');
      }

      // Normaliza formato: backend pode retornar { data: {...} } ou o objeto direto
      const apiResponse = importResult ?? {};
      // apiResponse may be { data: {...}, success: true } or the inner data directly
      const outerData = apiResponse?.data ?? apiResponse;
      // if outerData itself contains a nested data (double-wrapped), unwrap it
      const inner = outerData?.data ?? outerData;

      // Normalizado mas mantendo apenas os campos do tipo ImportacaoResultado
      const normalizedAny: any = {
        success: apiResponse?.success ?? true,
        message: apiResponse?.message ?? apiResponse?.msg ?? '',
        arquivo: inner?.arquivo ?? outerData?.arquivo ?? apiResponse?.arquivo ?? '',
        processados: inner?.processedCount ?? inner?.processados ?? outerData?.processados ?? apiResponse?.processados ?? 0,
        criados: inner?.created ?? inner?.criados ?? outerData?.criados ?? apiResponse?.criados ?? 0,
        atualizados: inner?.updated ?? inner?.atualizados ?? outerData?.atualizados ?? apiResponse?.atualizados ?? 0,
        erros: inner?.errorCount ?? inner?.erros ?? outerData?.erros ?? apiResponse?.erros ?? 0,
        detalhes: inner?.detalhesErros ?? inner?.detalhes ?? outerData?.detalhesErros ?? outerData?.detalhes ?? apiResponse?.detalhes ?? [],
      };

      // Special handling for análise de produção: backend often returns counts and an `errors` array inside inner
      if (type === 'analise') {
        const d = inner ?? outerData ?? apiResponse;
        const rawErrors = d?.errors ?? d?.erros ?? d?.errorsList ?? d?.errors ?? [];
        normalizedAny.processados = d?.processedCount ?? d?.processados ?? normalizedAny.processados;
        normalizedAny.criados = d?.created ?? d?.criados ?? normalizedAny.criados;
        normalizedAny.atualizados = d?.updated ?? d?.atualizados ?? normalizedAny.atualizados;
        normalizedAny.erros = d?.errorCount ?? d?.erros ?? normalizedAny.erros;

        normalizedAny.detalhes = Array.isArray(rawErrors)
          ? rawErrors.map((e: any, i: number) => {
              if (typeof e === 'string') return { row: i + 1, error: e, data: { raw: {} } };
              if (e && typeof e === 'object') {
                const row = e.row ?? e.linha ?? e.index ?? i + 1;
                const msg = e.message ?? e.error ?? e.mensagem ?? '';
                const raw = e.raw ?? e.data ?? e.payload ?? e;
                return { row, error: msg, data: { raw } };
              }
              return { row: i + 1, error: JSON.stringify(e), data: { raw: e } };
            })
          : [];

        normalizedAny.arquivo = d?.arquivo ?? normalizedAny.arquivo;
      }

      // Special handling for migração: backend returns error details differently  
      if (type === 'migracao') {
        const d = inner ?? outerData ?? apiResponse;
        const rawErrors = d?.details ?? d?.detalhes ?? d?.errors ?? d?.erros ?? [];
        normalizedAny.processados = d?.processedCount ?? d?.processados ?? normalizedAny.processados;
        normalizedAny.criados = d?.created ?? d?.criados ?? normalizedAny.criados;
        normalizedAny.atualizados = d?.updated ?? d?.atualizados ?? normalizedAny.atualizados;
        normalizedAny.erros = Array.isArray(rawErrors) ? rawErrors.length : 0;

        normalizedAny.detalhes = Array.isArray(rawErrors)
          ? rawErrors.map((e: any, i: number) => {
              if (typeof e === 'string') return { row: i + 1, error: e, data: { raw: {} } };
              if (e && typeof e === 'object') {
                const row = e.row ?? e.linha ?? e.index ?? i + 1;
                const msg = e.message ?? e.error ?? e.mensagem ?? e;
                const raw = e.raw ?? e.data ?? e.payload ?? {};
                return { row, error: msg, data: { raw } };
              }
              return { row: i + 1, error: JSON.stringify(e), data: { raw: {} } };
            })
          : [];

        normalizedAny.arquivo = d?.arquivo ?? normalizedAny.arquivo;
      }

      // Processar detalhes de erro para todos os tipos de importação
      if (!normalizedAny.detalhes || normalizedAny.detalhes.length === 0) {
        const rawErrors = inner?.errors ?? inner?.erros ?? outerData?.errors ?? outerData?.erros ?? apiResponse?.errors ?? apiResponse?.erros ?? [];
        if (Array.isArray(rawErrors) && rawErrors.length > 0) {
          normalizedAny.detalhes = rawErrors.map((e: any, i: number) => {
            if (typeof e === 'string') return { row: i + 1, error: e, data: { raw: {} } };
            if (e && typeof e === 'object') {
              const row = e.row ?? e.linha ?? e.index ?? i + 1;
              const msg = e.message ?? e.error ?? e.mensagem ?? '';
              const raw = e.raw ?? e.data ?? e.payload ?? e;
              return { row, error: msg, data: { raw } };
            }
            return { row: i + 1, error: JSON.stringify(e), data: { raw: e } };
          });
        }
      }

      // Guardar nome do arquivo separadamente (para uso no download)
      setArquivoNome(normalizedAny.arquivo || null);

      const minimal: ImportacaoResultado = {
        success: normalizedAny.success,
        message: normalizedAny.message,
        processados: normalizedAny.processados,
        criados: normalizedAny.criados,
        erros: normalizedAny.erros,
        detalhes: normalizedAny.detalhes,
      };

      setResult(minimal);
    } catch (err) {
      // Quando o apiClient lança um Error estendido com body, mostramos detalhes
      let message = 'Erro na importação';
      try {
        if (err instanceof Error) message = err.message;
        // alguns Errors carregam .body com o JSON retornado pelo backend
        if ((err as any)?.body) {
          const body = (err as any).body;
          // prefer explicit message from backend
          message = body.message || body.msg || body.error || JSON.stringify(body);
          setServerErrorBody(body);
        } else {
          setServerErrorBody(null);
        }
      } catch (e) {
        // fallback
        setServerErrorBody(null);
      }
      // log completo para o dev
      // eslint-disable-next-line no-console
      console.error('Import error:', err);
      setError(message);
    } finally {
      setLoading(false);
      // finish progress
      setProgress(100);
      if (progressTimer.current) {
        window.clearInterval(progressTimer.current);
        progressTimer.current = null;
      }
      // hide progress after a short delay so users can see completion
      setTimeout(() => setProgress(0), 600);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    setFile(selectedFile || null);
    if (error) setError('');
    if (result) setResult(null);
  };

  const handleReset = () => {
    setFile(null);
    setTableName('');
    setError('');
    setResult(null);
    if (progressTimer.current) {
      window.clearInterval(progressTimer.current);
      progressTimer.current = null;
    }
    setProgress(0);
  };

  useEffect(() => {
    return () => {
      if (progressTimer.current) window.clearInterval(progressTimer.current);
    };
  }, []);

  // Normalize server-provided error details (supports multiple key names)
  const serverDetails: any[] | null = serverErrorBody
    ? (serverErrorBody.details ?? serverErrorBody.detalhes ?? serverErrorBody.errors ?? serverErrorBody.erros ?? null)
    : null;

  return (
    <div className="p-6 rounded-lg" style={{ backgroundColor: '#FFFFFF', border: '1px solid #C3B4DE' }}>
      <div className="mb-6">
        <h3 className="text-xs font-semibold mb-2" style={{ color: '#3d2b56' }}>
          {title}
        </h3>
        {description && (
          <p className="text-xs" style={{ color: '#9E9E9E' }}>
            {description}
          </p>
        )}
      </div>

      {/* Progress bar (simulated) */}
      {progress > 0 && (
        <div className="mb-4" aria-hidden={false}>
          <div style={{ height: 8, background: '#EEE', borderRadius: 4, overflow: 'hidden' }}>
            <div
              style={{
                height: '100%',
                width: `${Math.max(2, Math.min(100, Math.round(progress))) }%`,
                background: progress < 100 ? '#7A61B2' : '#4CAF50',
                transition: 'width 300ms linear'
              }}
            />
          </div>
        </div>
      )}

      {!result ? (
        <form onSubmit={handleSubmit} className="space-y-2">
          <div>
            <label htmlFor={`file-${type}`} className="block text-xs font-medium mb-2" style={{ color: '#121212' }}>
              {type === 'migracao' ? 'Arquivo CSV *' : 'Arquivo Excel *'}
            </label>
            <input
              id={`file-${type}`}
              name="file"
              type="file"
              accept={acceptedFiles}
              onChange={handleFileChange}
              required
              className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
              style={{ 
                borderColor: '#C3B4DE', 
                backgroundColor: '#FFFFFF', 
                color: '#121212'
              }}
              onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
            />
            {file && (
              <p className="mt-1 text-xs" style={{ color: '#5d54a4' }}>
                Arquivo selecionado: {file.name}
              </p>
            )}
          </div>

          {type === 'migracao' && (
            <div>
              <label htmlFor={`tableName-${type}`} className="block text-xs font-medium mb-2" style={{ color: '#121212' }}>
                Nome da Tabela *
              </label>
              <input
                id={`tableName-${type}`}
                name="tableName"
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="Ex: Clientes, Apolices, etc."
                required
                className="block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-xs"
                style={{ 
                  borderColor: '#C3B4DE', 
                  backgroundColor: '#FFFFFF', 
                  color: '#121212'
                }}
                onFocus={(e) => e.target.style.borderColor = '#7A61B2'}
              />
            </div>
          )}

          {error && (
            <div className="p-3 rounded-md bg-red-50">
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}
          {serverDetails && Array.isArray(serverDetails) && (
            <div className="mt-2 overflow-x-auto border rounded-md bg-white">
              <table className="w-full text-xs table-fixed">
                <thead className="bg-gray-50 text-left">
                  <tr>
                    <th className="p-2 w-12">#</th>
                    <th className="p-2">Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {serverDetails.map((d: any, i: number) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="p-2 align-top">{i + 1}</td>
                      <td className="p-2 align-top">{typeof d === 'string' ? d : JSON.stringify(d)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !file || (type === 'migracao' && !tableName.trim())}
              className="px-2 py-1 text-xs font-medium text-white rounded-md hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
              style={{ backgroundColor: '#7A61B2' }}
            >
              {loading ? 'Importando...' : 'Importar'}
            </button>
            
            {(file || error) && (
              <button
                type="button"
                onClick={handleReset}
                className="px-2 py-1 text-xs font-medium rounded-md border transition-colors"
                style={{ 
                  borderColor: '#C3B4DE', 
                  color: '#5d54a4',
                  backgroundColor: 'transparent'
                }}
              >
                Limpar
              </button>
            )}
          </div>
        </form>
      ) : (
        <div className="space-y-2">
          <div className={`p-4 rounded-md ${result.success ? 'bg-green-50' : 'bg-red-50'}`}>
            <h4 className={`font-semibold ${result.success ? 'text-green-800' : 'text-red-800'}`}>
              {result.success ? '✅ Importação Concluída' : '❌ Erro na Importação'}
            </h4>
            <p className={`mt-1 text-xs ${result.success ? 'text-green-700' : 'text-red-700'}`}>
              {result.message}
            </p>
          </div>

          {result.success && (
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="p-3 rounded-md" style={{ backgroundColor: '#F4EFFA' }}>
                <p className="text-xs font-semibold" style={{ color: '#3d2b56' }}>
                  {result.processados}
                </p>
                <p className="text-xs" style={{ color: '#9E9E9E' }}>
                  Processados
                </p>
              </div>
              <div className="p-3 rounded-md bg-green-50">
                <p className="text-xs font-semibold text-green-800">
                  {result.criados}
                </p>
                <p className="text-xs text-green-600">
                  Criados
                </p>
              </div>
              <div className="p-3 rounded-md bg-orange-50">
                <p className="text-xs font-semibold text-orange-800">
                  {result.erros || 0}
                </p>
                <p className="text-xs text-orange-600">
                  Erros
                </p>
              </div>
            </div>
          )}

          {result.detalhes && result.detalhes.length > 0 && (
            <div className="mt-4">
              <h5 className="font-medium mb-2" style={{ color: '#3d2b56' }}>
                Detalhes:
              </h5>
              <div className="mb-2">
                <button
                  type="button"
                  onClick={() => {
                    const blob = new Blob([JSON.stringify(result.detalhes, null, 2)], { type: 'application/json' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    const filename = arquivoNome ? `${arquivoNome}-erros.json` : 'detalhes-erros.json';
                    a.download = filename;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-2 py-1 text-xs font-medium rounded-md border transition-colors"
                  style={{
                    borderColor: '#C3B4DE',
                    color: '#5d54a4',
                    backgroundColor: 'transparent'
                  }}
                >
                  Baixar erros (JSON)
                </button>
              </div>
              <div className="max-h-64 overflow-y-auto space-y-3">
                {/* Mostrar cada erro como um cartão legível */}
                {result.detalhes.map((detalhe: any, index: number) => {
                  const raw = detalhe?.data?.raw ?? {};
                  const rowNumber = detalhe?.row ?? '-';
                  const errorMsg = detalhe?.error ?? detalhe?.mensagem ?? 'Erro desconhecido';

                  return (
                    <div key={index} className="p-3 border rounded-md bg-white shadow-sm">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="text-sm font-semibold" style={{ color: '#3d2b56' }}>
                            Linha {rowNumber}
                          </div>
                          <div className="text-xs mt-1" style={{ color: '#b91c1c' }}>
                            {errorMsg}
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="text-indigo-600 underline text-xs"
                            onClick={() => {
                              const rawLocal = detalhe?.data?.raw ?? {};
                              // mesmo comportamento: abrir modal de apólice/documento para tipos específicos
                              if (type === 'propostas' || type === 'analise' || type === 'migracao') {
                                let a: any = {};

                                if (type === 'analise') {
                                  a = {
                                    tipoDocumento: rawLocal?.['Tipo de documento'] ?? rawLocal?.tipoDocumento ?? undefined,
                                    numeroDocumento: rawLocal?.['Proposta'] ?? rawLocal?.['proposta'] ?? rawLocal?.['Número proposta'] ?? rawLocal?.numeroDocumento ?? undefined,
                                    codigoProposta: rawLocal?.['Nº proposta cia'] ?? rawLocal?.['codigo proposta'] ?? rawLocal?.codigoProposta ?? undefined,
                                    numeroApolice: rawLocal?.['Apólice'] ?? rawLocal?.numeroApolice ?? undefined,
                                    numeroEndosso: rawLocal?.['Endosso'] ?? rawLocal?.numeroEndosso ?? undefined,
                                    numeroCalculo: rawLocal?.['Nº de controle'] ?? rawLocal?.numeroCalculo ?? undefined,
                                    inicioVigencia: rawLocal?.['Início de Vigência'] ?? rawLocal?.['Inicio de Vigência'] ?? rawLocal?.inicioVigencia ?? undefined,
                                    fimVigencia: rawLocal?.['Término de Vigência'] ?? rawLocal?.['Termino de Vigencia'] ?? rawLocal?.fimVigencia ?? undefined,
                                    dataEmissao: rawLocal?.['Data Emissão'] ?? rawLocal?.['Data emissão'] ?? rawLocal?.dataEmissao ?? undefined,
                                    dataEntrada: rawLocal?.['Data Entrada'] ?? rawLocal?.dataEntrada ?? undefined,
                                    dataCancelamento: rawLocal?.['Data Cancelamento'] ?? rawLocal?.dataCancelamento ?? undefined,
                                    premioLiquido: rawLocal?.['Prêmio'] ?? rawLocal?.['Premio'] ?? rawLocal?.premio ?? undefined,
                                    comissao: rawLocal?.['Comissão'] ?? rawLocal?.comissao ?? undefined,
                                    percentualComissao: rawLocal?.['Porcentagem'] ?? undefined,
                                    quantidadeParcelas: rawLocal?.['Quantidade de Parcelas'] ?? undefined,
                                    pagamentoId: rawLocal?.['Forma de pagto 1ª parc'] ?? rawLocal?.formaPagamento ?? undefined,
                                    motivoCancelamento: rawLocal?.['Motivo Cancelamento'] ?? rawLocal?.motivoCancelamento ?? undefined,
                                    observacoes: rawLocal?.observacoes ?? undefined,
                                    cliente: {
                                      nome: rawLocal?.['Cliente'] ?? rawLocal?.cliente ?? undefined,
                                      cpfCnpj: rawLocal?.['CPF/CNPJ'] ?? rawLocal?.['CPF'] ?? rawLocal?.['CNPJ'] ?? rawLocal?.['Cnpj/Cpf'] ?? rawLocal?.cpfCnpjCliente ?? undefined,
                                      email: rawLocal?.email ?? undefined,
                                      telefone: rawLocal?.telefone ?? undefined,
                                    },
                                    seguradora: rawLocal?.['Seguradora'] ?? rawLocal?.seguradora ? { nome: rawLocal?.['Seguradora'] ?? rawLocal?.seguradora } : undefined,
                                    produto: (rawLocal?.['Produto'] ?? rawLocal?.['Ramo'] ?? rawLocal?.produto ?? rawLocal?.ramo) ? { nome: rawLocal?.['Produto'] ?? rawLocal?.['Ramo'] ?? rawLocal?.produto ?? rawLocal?.ramo } : undefined,
                                    empresa: (rawLocal?.empresaId ?? rawLocal?.EmpresaId) ? { 
                                      id: rawLocal?.empresaId ?? rawLocal?.EmpresaId, 
                                      nome: rawLocal?.Empresa ?? undefined 
                                    } : undefined,
                                  };
                                } else if (type === 'propostas') {
                                  a = {
                                    tipoDocumento: rawLocal?.['tipo doc'] ?? rawLocal?.tipoDocumento ?? undefined,
                                    numeroDocumento: rawLocal?.['proposta'] ?? rawLocal?.['proposta cia'] ?? rawLocal?.numeroDocumento ?? undefined,
                                    codigoProposta: rawLocal?.['proposta cia'] ?? rawLocal?.['codigo proposta'] ?? rawLocal?.codigo ?? undefined,
                                    numeroApolice: rawLocal?.['apólice'] ?? rawLocal?.['apolice'] ?? rawLocal?.['nº apólice'] ?? rawLocal?.['numero apolice'] ?? undefined,
                                    numeroCalculo: rawLocal?.['nº calculo'] ?? rawLocal?.['numero calcul'] ?? rawLocal?.['nº cálculo'] ?? rawLocal?.['numero calculo'] ?? rawLocal?.['nº calculo'] ?? rawLocal?.['numero calcul'] ?? undefined,
                                    inicioVigencia: rawLocal?.['vigência'] ?? rawLocal?.['vigencia'] ?? rawLocal?.['data proposta'] ?? undefined,
                                    fimVigencia: rawLocal?.['término de vigência'] ?? rawLocal?.['data fim vigencia'] ?? rawLocal?.['data fim vigência'] ?? rawLocal?.['datafimvigencia'] ?? undefined,
                                    dataEmissao: rawLocal?.['vigência'] ?? rawLocal?.['vigencia'] ?? rawLocal?.['data proposta'] ?? undefined,
                                    dataEntrada: rawLocal?.['data de entrada'] ?? rawLocal?.['data entrada'] ?? undefined,
                                    dataCancelamento: rawLocal?.['data cancelamento'] ?? undefined,
                                    premioLiquido: rawLocal?.['premio'] ?? rawLocal?.['premio liq'] ?? rawLocal?.['prêmio liq'] ?? rawLocal?.['premio liquido'] ?? undefined,
                                    comissao: rawLocal?.['comissão'] ?? rawLocal?.['comissao'] ?? rawLocal?.['valor comissao'] ?? undefined,
                                    quantidadeParcelas: rawLocal?.['parc'] ?? rawLocal?.['parcelas'] ?? rawLocal?.['quantidade parcelas'] ?? undefined,
                                    pagamentoId: rawLocal?.['meio de pagamento'] ?? rawLocal?.['meio pagamento'] ?? undefined,
                                    motivoCancelamento: rawLocal?.['motivo cancelamento'] ?? undefined,
                                    observacoes: rawLocal?.['observa'] ?? rawLocal?.['observação'] ?? rawLocal?.observacao ?? undefined,
                                    cliente: {
                                      nome: rawLocal?.['cliente'] ?? undefined,
                                      cpfCnpj: rawLocal?.['cnpj'] ?? rawLocal?.['cpf'] ?? rawLocal?.['cnpj/cpf'] ?? undefined,
                                      email: rawLocal?.email ?? undefined,
                                      telefone: rawLocal?.telefone ?? undefined,
                                    },
                                    seguradora: rawLocal?.['seguradora'] ? { nome: rawLocal?.['seguradora'] } : undefined,
                                    produto: rawLocal?.['produto'] ? { nome: rawLocal?.['produto'] } : undefined,
                                    empresa: (rawLocal?.empresaId ?? rawLocal?.EmpresaId) ? { 
                                      id: rawLocal?.empresaId ?? rawLocal?.EmpresaId, 
                                      nome: rawLocal?.Empresa ?? undefined 
                                    } : undefined,
                                  };
                                } else if (type === 'migracao') {
                                  a = {
                                    tipoDocumento: rawLocal?.tipoDocumento ?? rawLocal?.tipo ?? undefined,
                                    numeroDocumento: rawLocal?.numeroDocumento ?? rawLocal?.numero ?? rawLocal?.proposta ?? undefined,
                                    codigoProposta: rawLocal?.codigoProposta ?? rawLocal?.codigo ?? undefined,
                                    numeroApolice: rawLocal?.numeroApolice ?? rawLocal?.apolice ?? undefined,
                                    numeroEndosso: rawLocal?.numeroEndosso ?? rawLocal?.endosso ?? undefined,
                                    numeroCalculo: rawLocal?.numeroCalculo ?? rawLocal?.calculo ?? undefined,
                                    inicioVigencia: rawLocal?.inicioVigencia ?? rawLocal?.vigenciaInicio ?? undefined,
                                    fimVigencia: rawLocal?.fimVigencia ?? rawLocal?.vigenciaFim ?? undefined,
                                    dataEmissao: rawLocal?.dataEmissao ?? rawLocal?.emissao ?? undefined,
                                    dataEntrada: rawLocal?.dataEntrada ?? rawLocal?.entrada ?? undefined,
                                    dataCancelamento: rawLocal?.dataCancelamento ?? rawLocal?.cancelamento ?? undefined,
                                    premioLiquido: rawLocal?.premioLiquido ?? rawLocal?.premio ?? undefined,
                                    comissao: rawLocal?.comissao ?? undefined,
                                    percentualComissao: rawLocal?.percentualComissao ?? rawLocal?.percentual ?? undefined,
                                    quantidadeParcelas: rawLocal?.quantidadeParcelas ?? rawLocal?.parcelas ?? undefined,
                                    pagamentoId: rawLocal?.formaPagamento ?? rawLocal?.pagamento ?? undefined,
                                    motivoCancelamento: rawLocal?.motivoCancelamento ?? rawLocal?.motivo ?? undefined,
                                    observacoes: rawLocal?.observacoes ?? rawLocal?.obs ?? undefined,
                                    cliente: {
                                      nome: rawLocal?.nome ?? rawLocal?.cliente ?? undefined,
                                      cpfCnpj: rawLocal?.cpfCnpj ?? rawLocal?.cpf ?? rawLocal?.cnpj ?? undefined,
                                      email: rawLocal?.email ?? undefined,
                                      telefone: rawLocal?.telefone ?? undefined,
                                    },
                                    seguradora: rawLocal?.seguradora ? { nome: rawLocal?.seguradora } : undefined,
                                    produto: rawLocal?.produto ? { nome: rawLocal?.produto } : undefined,
                                    empresa: (rawLocal?.empresaId ?? rawLocal?.EmpresaId) ? { 
                                      id: rawLocal?.empresaId ?? rawLocal?.EmpresaId, 
                                      nome: rawLocal?.Empresa ?? undefined 
                                    } : undefined,
                                  };
                                }

                                // Limpa undefined
                                Object.keys(a).forEach(key => {
                                  if (a[key] === undefined) delete a[key];
                                  else if (typeof a[key] === 'object' && a[key] !== null) {
                                    Object.keys(a[key]).forEach(subKey => {
                                      if (a[key][subKey] === undefined) delete a[key][subKey];
                                    });
                                  }
                                });

                                a.raw = rawLocal;
                                setApoliceData(a);
                                setApoliceModalOpen(true);
                                setModalIndex(index);
                              } else {
                                setModalRaw(JSON.parse(JSON.stringify(rawLocal)));
                                setModalIndex(index);
                                setModalOpen(true);
                              }
                            }}
                          >
                            Editar
                          </button>
                        </div>
                      </div>

                      <div className="mt-3 text-xs text-gray-700">
                        {Object.keys(raw).length === 0 ? (
                          <div className="text-gray-500">Sem dados associados a essa linha.</div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(raw).slice(0, 12).map(([k, v]) => (
                              <div key={k} className="break-words">
                                <span className="font-medium text-gray-600">{k}:</span>{' '}
                                <span className="text-gray-800">{v === null || v === undefined ? <em className="text-gray-400">(vazio)</em> : String(v)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

              {/* Modal de edição de raw */}
              {modalOpen && modalIndex !== null && (
                <div className="fixed inset-0 flex items-center justify-center z-50">
                  <div className="absolute inset-0 bg-black opacity-50 z-40" onClick={() => setModalOpen(false)} />
                  <div className="bg-white rounded p-4 relative z-50 w-11/12 max-w-2xl shadow-lg">
                    <h4 className="font-semibold mb-2">Editar linha {result?.detalhes?.[modalIndex]?.row ?? ''}</h4>
                    <div className="max-h-96 overflow-y-auto p-2">
                      {/* Mapear raw para cliente esperado pelo ClienteForm */}
                      {modalRaw && (
                      <ClienteForm
                          initialValues={{
                            // mapear campos básicos (suporta múltiplos formatos)
                            cpfCnpj: modalRaw?.['CGC/CPF'] ?? modalRaw?.cpfCnpj ?? modalRaw?.cpf ?? modalRaw?.CPF ?? modalRaw?.cnpj ?? modalRaw?.CNPJ ?? '',
                            nome: modalRaw?.Nome ?? modalRaw?.nome ?? modalRaw?.cliente ?? modalRaw?.Cliente ?? '',
                            email: modalRaw?.Email ?? modalRaw?.email ?? '',
                            empresaId: modalRaw?.empresaId ?? modalRaw?.EmpresaId ?? arquivoNome ?? '',
                            apelido: modalRaw?.['Nome preferencial'] ?? modalRaw?.apelido ?? modalRaw?.nomePreferencial ?? '',
                            telefone: modalRaw?.Telefone ?? modalRaw?.telefone ?? '',
                            dataNascimentoAbertura: modalRaw?.['Data de Nascimento/Dt. abertura/fundação'] ?? modalRaw?.dataNascimento ?? modalRaw?.dataNascimentoAbertura ?? '',
                            codigoExterno: modalRaw?.['Código externo'] ?? modalRaw?.codigoExterno ?? modalRaw?.codigo ?? '',
                            // outros campos serão ignorados e podem ser preenchidos manualmente no formulário
                          } as any}
                          // overrideSubmit envia para /api/cliente conforme solicitado
                          overrideSubmit={async (apiData: any) => {
                            // enviar para /clientes (o apiClient já adiciona base URL e headers)
                            const res = await apiClient.request('/clientes', {
                              method: 'POST',
                              body: JSON.stringify(apiData),
                            });
                            return res;
                          }}
                          onSuccess={(savedCliente: any) => {
                            // Atualizar detalhe com os dados salvos
                            const updated = Array.isArray(result?.detalhes) ? [...(result!.detalhes as any[])] : [];
                            const target = updated[modalIndex!] ?? {};
                            target.data = { ...(target.data || {}), raw: { ...target.data?.raw, ...savedCliente } };
                            target.error = 'Corrigido manualmente';
                            updated[modalIndex!] = target;
                            setResult((prev) => prev ? ({ ...prev, detalhes: updated }) : prev);
                            setModalOpen(false);
                          }}
                          onCancel={() => setModalOpen(false)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {apoliceModalOpen && apoliceData && (
                <ApoliceFormModal
                  apolice={apoliceData}
                  isOpen={apoliceModalOpen}
                  isEditing={true}
                  onClose={() => {
                    setApoliceModalOpen(false);
                    setApoliceData(null);
                  }}
                  onSave={async (a: any) => {
                    try {
                      // if we have an id, update, otherwise create
                      let saved: any;
                      if (a?.id) {
                        const res = await documentoService.update(a.id, a);
                        saved = res?.data ?? res;
                      } else {
                        const res = await documentoService.create(a);
                        saved = res?.data ?? res;
                      }

                      // Atualizar detalhe local com dados do documento salvo
                      const updated = Array.isArray(result?.detalhes) ? [...(result!.detalhes as any[])] : [];
                      const target = updated[modalIndex!] ?? {};
                      target.data = { ...(target.data || {}), raw: { ...target.data?.raw, ...saved } };
                      target.error = 'Corrigido manualmente';
                      updated[modalIndex!] = target;
                      setResult((prev) => prev ? ({ ...prev, detalhes: updated }) : prev);
                      setApoliceModalOpen(false);
                      setApoliceData(null);
                    } catch (err: any) {
                      // Show a clearer message when the backend reports a duplicate-key
                      // Do NOT perform any automatic update here; just warn the user.
                      const backendMessage = err?.response?.data?.message ?? err?.message ?? String(err);
                      const isDuplicate = typeof backendMessage === 'string' && (
                        backendMessage.includes('Registro duplicado') ||
                        backendMessage.toLowerCase().includes('duplicate') ||
                        backendMessage.includes('Documento_numeroDocumento_key')
                      );

                      if (isDuplicate) {
                        const numero = (apoliceData && (apoliceData.numeroDocumento || apoliceData.numeroApolice)) || (a && (a.numeroDocumento || a.numeroApolice)) || 'desconhecido';
                        // Inform the user that a document with this number already exists and that
                        // the import does not populate the 'observacoes' field.
                        notifier.show({ 
                          message: `Documento duplicado (número: ${numero}). Use a importação apenas para novos documentos.`,
                          severity: 'warning'
                        });
                      } else {
                        // fallback: generic error
                        notifier.show({ message: backendMessage || 'Erro ao salvar documento', severity: 'error' });
                      }
                    }
                  }}
                />
              )}

          <button
            onClick={handleReset}
            className="w-full px-2 py-1 text-xs font-medium rounded-md border transition-colors"
            style={{ 
              borderColor: '#C3B4DE', 
              color: '#5d54a4',
              backgroundColor: 'transparent'
            }}
          >
            Nova Importação
          </button>
        </div>
      )}
    </div>
  );
};